"use client";

import React from "react";
import {
  Info,
  Gift,
  ShoppingBag,
  CreditCard,
  Truck,
  AlertTriangle,
  MessageCircle,
  ShieldCheck,
  MapPin,
  Clock,
  ArrowRight,
} from "lucide-react";

export const OrderGuideSection = () => {
  return (
    <div className="mt-16 space-y-12 pb-10">
      {/* Section Header */}
      <div className="text-center space-y-4">
        <div className="text-[10px] font-bold text-gold-core uppercase tracking-[0.4em] flex items-center justify-center gap-4 before:h-px before:w-12 before:bg-gold-dim after:h-px after:w-12 after:bg-gold-dim">
          Essential Information
        </div>
        <h2 className="font-cormorant text-4xl text-text-1">
          Order <em>Guide</em> & Support
        </h2>
      </div>

      {/* Login Benefit - Hero Card */}
      <div className="relative group overflow-hidden">
        <div className="absolute inset-0 bg-gold-gradient opacity-10 blur-3xl group-hover:opacity-20 transition-opacity duration-700" />
        <div className="relative bg-black-2 border border-gold-dim/20 p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-full bg-gold-core/10 flex items-center justify-center text-gold-core border border-gold-dim/30">
              <Gift size={32} />
            </div>
            <div>
              <h3 className="text-xl font-cormorant text-text-1 mb-1">
                Exclusive Welcome Offer
              </h3>
              <p className="text-text-3 text-md">
                Experience the elegance of Nakman with a special gift.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-center md:items-end gap-2">
            <div className="bg-gold-core text-black px-6 py-2 text-[11px] font-bold uppercase tracking-widest rounded-full shadow-[0_0_20px_rgba(212,175,55,0.3)]">
              🎉 5% Login Bonus on First Order
            </div>
            <p className="text-[10px] text-gold-core/80 font-medium tracking-widest uppercase">
              Min. Order Value: ₹3,000
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Order Guidelines */}
        <div className="bg-black-2 border border-border-1 p-8 space-y-8 h-full">
          <div className="flex items-center gap-3 border-b border-border-1 pb-4">
            <ShoppingBag className="text-gold-dim" size={20} />
            <h3 className=" font-bold uppercase tracking-widest text-text-1">
              Order Guidelines
            </h3>
          </div>
          <ul className="space-y-6">
            <li className="flex gap-4 group">
              <div className="mt-1 w-1.5 h-1.5 rounded-full bg-gold-dim group-hover:scale-150 transition-transform shrink-0" />
              <p className="text-text-2 text-md leading-relaxed">
                Customers are requested to{" "}
                <strong className="text-text-1">
                  carefully review products
                </strong>{" "}
                before placing an order.
              </p>
            </li>
            <li className="flex gap-4 group">
              <div className="mt-1 w-1.5 h-1.5 rounded-full bg-gold-dim group-hover:scale-150 transition-transform shrink-0" />
              <p className="text-text-2 text-md leading-relaxed">
                Read{" "}
                <strong className="text-text-1">product descriptions</strong>{" "}
                and view images thoroughly to understand craftsmanship.
              </p>
            </li>
            <li className="flex gap-4 group">
              <div className="mt-1 w-1.5 h-1.5 rounded-full bg-gold-dim group-hover:scale-150 transition-transform shrink-0" />
              <p className="text-text-2 text-md leading-relaxed">
                Verify{" "}
                <strong className="text-text-1">
                  final price and order summary
                </strong>{" "}
                before clicking checkout.
              </p>
            </li>
            <li className="flex gap-4 group">
              <div className="mt-1 w-1.5 h-1.5 rounded-full bg-gold-dim group-hover:scale-150 transition-transform shrink-0" />
              <p className="text-text-2 text-md leading-relaxed">
                Provide a{" "}
                <strong className="text-text-1">
                  complete and accurate delivery address
                </strong>{" "}
                to avoid shipping delays.
              </p>
            </li>
          </ul>
        </div>

        {/* Payment Instructions */}
        <div className="bg-black-2 border border-border-1 p-8 space-y-8 h-full">
          <div className="flex items-center gap-3 border-b border-border-1 pb-4">
            <CreditCard className="text-gold-dim" size={20} />
            <h3 className="font-bold uppercase tracking-widest text-text-1">
              Payment Instructions
            </h3>
          </div>

          <div className="flex flex-wrap gap-4">
            <div className="bg-red-500/10 border border-red-500/20 px-4 py-2 flex items-center gap-2">
              <span className="text-red-500 text-[13px] font-bold uppercase tracking-widest">
                ❌ No COD
              </span>
            </div>
            <div className="bg-green-500/10 border border-green-500/20 px-4 py-2 flex items-center gap-2">
              <span className="text-green-500 text-[13px] font-bold uppercase tracking-widest">
                ✅ Online Payment Only
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-[13px] text-text-3 font-bold uppercase tracking-widest">
              Steps to Pay:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  step: "01",
                  title: "Place Order",
                  desc: "Select items & checkout",
                },
                {
                  step: "02",
                  title: "Pay via UPI",
                  desc: "Scan QR or Use UPI ID",
                },
                {
                  step: "03",
                  title: "Upload Proof",
                  desc: "Submit screenshot",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="bg-black/40 p-4 border border-border-1 relative group hover:border-gold-dim transition-colors"
                >
                  <span className="absolute top-2 right-3 text-[18px] font-cormorant italic text-gold-dim/20">
                    {item.step}
                  </span>
                  <p className="text-[13px] text-text-1 font-bold uppercase mb-1">
                    {item.title}
                  </p>
                  <p className="text-[11px] text-text-3 leading-tight">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-yellow-500/10 border-l-2 border-yellow-500 p-4 flex items-start gap-3">
            <AlertTriangle className="text-yellow-500 shrink-0" size={18} />
            <p className="text-sm text-text-2 leading-relaxed">
              <strong className="text-text-1 font-bold uppercase tracking-widest">
                Confirmation Rule:
              </strong>
              <br />
              Order will be confirmed only after{" "}
              <span className="text-text-1">payment verification</span> by the
              Nakman team. Partial or missing payments will cause cancellations.
            </p>
          </div>
        </div>
      </div>

      {/* Delivery & Logistics */}
      <div className="bg-black-2 border border-border-1 p-8 md:p-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="space-y-4">
            <div className="w-10 h-10 bg-black flex items-center justify-center text-gold-core border border-border-1">
              <MapPin size={18} />
            </div>
            <div>
              <h4 className="text-sm text-text-1 font-bold uppercase tracking-widest mb-1">
                Dispatch Origin
              </h4>
              <p className="text-text-3 text-xs">
                Orders are sent from{" "}
                <strong className="text-text-1">Ahmedabad</strong>
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="w-10 h-10 bg-black flex items-center justify-center text-gold-core border border-border-1">
              <Clock size={18} />
            </div>
            <div>
              <h4 className="text-sm text-text-1 font-bold uppercase tracking-widest mb-1">
                Dispatch Time
              </h4>
              <p className="text-text-3 text-xs">
                <strong className="text-text-1">1–3 Business Days</strong>
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="w-10 h-10 bg-black flex items-center justify-center text-gold-core border border-border-1">
              <Truck size={18} />
            </div>
            <div>
              <h4 className="text-sm text-text-1 font-bold uppercase tracking-widest mb-1">
                Delivery Time
              </h4>
              <p className="text-text-3 text-xs">
                <strong className="text-text-1">3–10 Working Days</strong>{" "}
                (Varies by location)
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="w-10 h-10 bg-black flex items-center justify-center text-gold-core border border-border-1">
              <ShieldCheck size={18} />
            </div>
            <div>
              <h4 className="text-sm text-text-1 font-bold uppercase tracking-widest mb-1">
                Shipping Policy
              </h4>
              <p className="text-text-3 text-xs">
                Applicable on ALL wholesale orders.{" "}
                <strong className="text-text-1">No free shipping.</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border-1 flex flex-col sm:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-4 text-text-3 italic text-sm">
            <span className="w-10 h-px bg-border-1" />
            Tracking information will be shared via WhatsApp once dispatched.
            <span className="w-10 h-px bg-border-1" />
          </div>
        </div>
      </div>

      {/* Support Section */}
      <div className="bg-gold-core/5 border border-gold-dim/20 p-10 text-center space-y-8 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-gold-pool opacity-10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-gold-pool opacity-10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-2xl mx-auto space-y-4">
          <h3 className="font-cormorant text-4xl text-text-1">
            Need <em>Help?</em>
          </h3>
          <p className="text-text-2 leading-relaxed">
            For any concerns after receiving your order, or for payment
            assistance, our dedicated support team is here to help you.
          </p>

          <div className="pt-4 flex flex-col items-center gap-6">
            <div className="space-y-1">
              <p className="text-[10px] text-text-3 uppercase tracking-[0.2em] font-bold">
                WhatsApp Support
              </p>
              <p className="text-2xl font-medium text-text-1 tracking-widest">
                +91 93279 99188
              </p>
            </div>

            <a
              href="https://wa.me/919327999188?text=Hello,%20I%20need%20support%20regarding%20my%20order."
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center gap-3 bg-gold-core text-black px-10 py-4 text-[11px] font-bold uppercase tracking-[0.3em] overflow-hidden transition-all hover:bg-gold-bright"
            >
              <MessageCircle size={18} />
              Chat on WhatsApp
              <ArrowRight
                size={16}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
